import { useState } from "react";
import { Loader2, Upload } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";

import type { ChecklistItem, FieldSpec } from "../../api/types";
import { rowLabel } from "../../model/collect";
import {
  rowEditorKind,
  appendAssetValue,
  rowImageBlocked,
  rowImageTarget,
  rowInitialText,
  rowInputValue,
  type ImageUploadTarget,
} from "../../model/rowEdit";
import { optionLabel } from "../../model/schemaLabels";
import { CropUploadDialog } from "./host/CropUploadDialog";

/**
 * 资料清单一行的行内编辑(0925 B7):按字段类型给输入框 —— 文字 / 多行 / 列表(每行一条)/ 枚举下拉 / 图片上传。
 * 保存 = 调用方发 `PUT collect/answers`(`row:{path}` 题号,A.30 形状,见 `model/rowEdit.ts`),回 true 就收起。
 * 图片行:裁剪上传(A.53)拿到完整 url 后直接保存成这一格的值;不能传的(模拟发布 / 没有对应用途)按钮禁用并写原因。
 */
export function ChecklistRowEditor({
  item,
  field,
  hostMode,
  disabled,
  onSave,
  onCancel,
  upload,
}: {
  item: ChecklistItem;
  field: FieldSpec | undefined;
  hostMode: boolean;
  disabled?: boolean;
  onSave: (value: unknown) => Promise<boolean>;
  onCancel: () => void;
  upload: (target: ImageUploadTarget, base64: string) => Promise<string>;
}) {
  const kind = rowEditorKind(item.type ?? field?.type);
  const label = rowLabel(item);
  const [text, setText] = useState(() => rowInitialText(item.value));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputId = `row-edit-${item.path}`;

  const save = async (value: unknown) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(value);
    } finally {
      setSaving(false);
    }
  };

  const submit = () => {
    const value = rowInputValue(kind, text, field);
    if (value == null) {
      setError(`「${label}」不能为空`);
      return;
    }
    void save(value);
  };

  const busy = disabled || saving;

  if (kind === "image" || kind === "asset_list") {
    const blocked = rowImageBlocked(item.path, hostMode);
    const target = rowImageTarget(item.path);
    return (
      <div
        role="group"
        aria-label={`编辑:${label}`}
        className="mt-1 space-y-1.5 rounded-md border border-border bg-muted/30 p-2"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            disabled={busy || blocked != null}
            title={blocked ?? undefined}
            onClick={() => setUploading(true)}
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            上传图片
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={onCancel}
          >
            取消
          </Button>
        </div>
        {blocked && <p className="text-2xs text-muted-foreground">{blocked}</p>}
        {target && (
          <CropUploadDialog
            open={uploading}
            onOpenChange={(o) => {
              if (!o) setUploading(false);
            }}
            title={`上传 · ${label}`}
            defaultAspect={
              target.purpose === "logo" ? 1 : target.purpose === "hero" ? 16 / 9 : null
            }
            upload={(b64) => upload(target, b64)}
            onDone={(url) =>
              void save(kind === "asset_list" ? appendAssetValue(item.value, url) : url)
            }
          />
        )}
      </div>
    );
  }

  return (
    <form
      aria-label={`编辑:${label}`}
      className="mt-1 space-y-1.5 rounded-md border border-border bg-muted/30 p-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label htmlFor={inputId} className="sr-only">
        {label}
      </label>
      {kind === "enum" ? (
        <select
          id={inputId}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
        >
          <option value="">请选择</option>
          {(field?.options ?? []).map((o) => (
            <option key={o} value={o}>
              {optionLabel(o)}
            </option>
          ))}
        </select>
      ) : kind === "text" ? (
        <Input
          id={inputId}
          value={text}
          maxLength={field?.maxLen ?? undefined}
          onChange={(e) => setText(e.target.value)}
          className="h-8 text-xs"
        />
      ) : (
        <Textarea
          id={inputId}
          value={text}
          rows={kind === "list" ? 3 : 2}
          placeholder={
            kind === "list" ? `每行一条${field?.max ? `,最多 ${field.max} 条` : ""}` : undefined
          }
          onChange={(e) => setText(e.target.value)}
          className="text-xs"
        />
      )}
      {error && (
        <p role="alert" className="text-2xs text-destructive">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={onCancel}
        >
          取消
        </Button>
        <Button type="submit" size="sm" className="h-7 px-2 text-xs" disabled={busy}>
          {saving && <Loader2 className="h-3 w-3 animate-spin" />}
          保存
        </Button>
      </div>
    </form>
  );
}
